import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  setAdminManualFinanceParams,
  setResetApplicationsStatusList,
  showFinancePopup,
} from 'store/applications';
import { useCustomNavigation } from 'hooks/useCustomNavigation';
import {
  checkNewDealForDealer,
  inviteClientToDeal,
  returnDealToPreviousStatus,
} from 'data-providers/applicationsProvider';
import { isCorrectBtnStatus } from 'components/modules/Applications/helpers';
import * as metrica from 'metrika/applications';

const CLIENT_INVITED_DEAL = 'client-invited-deal';

export const useDealActionButtonsPresenter = ({ deal, application }) => {
  const [isShowButton, setShowButton] = useState(false);
  const [buttons, setButtons] = useState([]);
  const rootDispatch = useDispatch();
  const { openDealParametersPage } = useCustomNavigation();

  const isUserAdmin = useSelector((state) => state.user.role) === 3406;
  const isReadyToBeSentToBanks = application?.orders[0].body.isReadyToBeSentToBanks ?? false;
  const applicStatus = application?.lastState.state;
  const dealStatus = deal.state.state;

  const isShowActionButton = isShowButton && buttons.length > 0;
  const stored = localStorage.getItem('token');
  const { credit: applicationId, loan } = application?.ids || {};
  const isAplicStatusClientInvitedDeal =
    application?.orders[0].body.applicationState.state === CLIENT_INVITED_DEAL;

  const BUTTON_STATUS_GROUPS = {
    FINANCE: 'financeBtnStatus',
    BACK_LOAN: 'backLoanIssueBtnStatus',
    ENTER_FINAL_PARAMS: 'enterFinalParamsBtnStatus',
    INVITE_CLIENT: 'inviteClientToDealBtnStatus',
  };

  const ACTION_BUTTONS = {
    fundAndCloseBtn: {
      id: 'fundAndCloseBtn',
      title: 'Профинансировать и закрыть',
      clickHandler: async () => {
        metrica.dealActionFinanceButtonTrack();
        const worksheetId = application.id;
        const url = `/worksheet/${worksheetId}/final-params`;
        if (!isAplicStatusClientInvitedDeal) {
          await inviteClientToDeal(deal.id);
        }
        openDealParametersPage(application, url);
      },
    },
    createLoanIssueBtn: {
      id: 'createLoanIssueBtn',
      title: 'Оформить цифровую сделку',
      hint: 'К сожалению, вы не аккредитованы в банке для проведения сделки. Обратитесь в поддержку за пояснением',
      clickHandler: async () => {
        metrica.dealActionCreateLoanIssueButtonTrack();
        if (!isAplicStatusClientInvitedDeal) {
          await inviteClientToDeal(deal.id);
        }
        window.location.href = `${
          process.env.REACT_APP_API_ENTRYPOINT
        }/api/order/loan-issue/${applicationId}/${deal.id}?jwt=${JSON.parse(stored).token}`;
      },
    },
    financeBtn: {
      id: 'financeBtn',
      title: 'Профинансировать',
      clickHandler: () => {
        rootDispatch(showFinancePopup());
        rootDispatch(
          setAdminManualFinanceParams({
            bankId: deal?.bank?.id,
            dealId: deal.id,
            dealerName: application.dealer.name,
          })
        );
      },
    },
    enterFinalParamsBtn: {
      id: 'enterFinalParamsBackBtn',
      title: 'Внести итоговые параметры',
      clickHandler: () => {
        metrica.dealActionEnterFinalParamsBackButtonTrack();
        const url = `/worksheet/${application.id}/final-params`;
        openDealParametersPage(application, url);
      },
    },
    backLoanIssueBtn: {
      id: 'backLoanIssueBtn',
      title: 'Вернуться в цифровую сделку',
      clickHandler: () => {
        const url = `/worksheet/${application.id}/loan-issue`;
        openDealParametersPage(application, url);
      },
    },
    returnToPreviousStatusBtn: {
      id: 'returnToPreviousStatusBtn',
      title: 'Вернуть предыдущий статус',
      clickHandler: async () => {
        rootDispatch(
          setResetApplicationsStatusList({
            applicationId: applicationId,
            isResetFetching: true,
          })
        );

        try {
          await returnDealToPreviousStatus(deal.id);
        } catch (err) {
          rootDispatch(
            setResetApplicationsStatusList({
              applicationId: applicationId,
              isResetFetching: false,
            })
          );
        }
      },
    },
  };

  const getButtonList = useCallback(async () => {
    const buttonList = [];

    const isAdminAndNotFinalStatus =
      isUserAdmin && isCorrectBtnStatus(BUTTON_STATUS_GROUPS.FINANCE, applicStatus);

    const shouldShowDealInitiationBtn =
      (isCorrectBtnStatus(BUTTON_STATUS_GROUPS.INVITE_CLIENT, dealStatus) &&
        !isAplicStatusClientInvitedDeal) ||
      (dealStatus === CLIENT_INVITED_DEAL && isAplicStatusClientInvitedDeal);

    if (loan && isCorrectBtnStatus(BUTTON_STATUS_GROUPS.BACK_LOAN, dealStatus)) {
      buttonList.push(ACTION_BUTTONS.backLoanIssueBtn);
    } else {
      if (shouldShowDealInitiationBtn) {
        buttonList.push(ACTION_BUTTONS.fundAndCloseBtn);
      }

      if (shouldShowDealInitiationBtn && isReadyToBeSentToBanks) {
        const isDisabled = (await checkNewDealForDealer(deal.id)) !== 'redirect_new_deal';
        buttonList.push({
          ...ACTION_BUTTONS.createLoanIssueBtn,
          disabled: isDisabled,
        });
      }
    }

    if (isCorrectBtnStatus(BUTTON_STATUS_GROUPS.ENTER_FINAL_PARAMS, dealStatus) && !loan) {
      buttonList.push(ACTION_BUTTONS.enterFinalParamsBtn);
    }

    if (isAdminAndNotFinalStatus) {
      buttonList.push(ACTION_BUTTONS.financeBtn);
    }

    if (isAdminAndNotFinalStatus && !loan && applicationId) {
      buttonList.push(ACTION_BUTTONS.returnToPreviousStatusBtn);
    }

    setButtons(buttonList);
  }, [deal, application, isUserAdmin, loan]);

  useEffect(() => {
    getButtonList();
  }, [dealStatus, applicStatus]);

  const handleMouseEnter = () => {
    setShowButton(true);
  };

  const handleMouseLeave = () => {
    setShowButton(false);
  };

  return {
    buttons,
    isShowButton,
    isShowActionButton,
    handleMouseEnter,
    handleMouseLeave,
  };
};
